import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  chatKnowledge,
  exportKnowledge,
  importKnowledgeSnapshot,
  type KnowledgeChatMessage,
  type KnowledgeEdge,
  type KnowledgeEntity,
  type KnowledgePendingMutation,
  type KnowledgeSnapshot,
} from '@/entities/knowledge';
import { ApiError } from '@/shared/api';
import { PageHeader } from '@/widgets/page-header';
import {
  KnowledgeGraphCanvas,
  type KnowledgeGraphSelection,
} from '@/widgets/knowledge-graph';
import { KnowledgeChatPanel } from '@/widgets/knowledge-chat';
import { KnowledgeDetailsPanel } from '@/widgets/knowledge-details';
import { Button } from '@/shared/ui/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

type SideTab = 'chat' | 'details';

function snapshotFilename(): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `knowledge-snapshot-${stamp}.json`;
}

function downloadSnapshotFile(snapshot: KnowledgeSnapshot) {
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = snapshotFilename();
  anchor.click();
  URL.revokeObjectURL(url);
}

function parseSnapshotFile(raw: string): {
  entities: KnowledgeEntity[];
  edges: KnowledgeEdge[];
} {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON file');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Snapshot must be a JSON object');
  }
  const body = parsed as Record<string, unknown>;
  if (!Array.isArray(body.entities) || body.entities.length === 0) {
    throw new Error('Snapshot must include a non-empty entities array');
  }
  const edges = Array.isArray(body.edges) ? (body.edges as KnowledgeEdge[]) : [];
  return {
    entities: body.entities as KnowledgeEntity[],
    edges,
  };
}

export function KnowledgePage() {
  const queryClient = useQueryClient();
  const restoreInputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<KnowledgeGraphSelection | null>(null);
  const [sideTab, setSideTab] = useState<SideTab>('chat');
  const [messages, setMessages] = useState<KnowledgeChatMessage[]>([]);
  const [pending, setPending] = useState<KnowledgePendingMutation[]>([]);
  const [draft, setDraft] = useState('');
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  const snapshotQuery = useQuery({
    queryKey: ['knowledge', 'export'],
    queryFn: exportKnowledge,
  });

  const selectedEntity = useMemo((): KnowledgeEntity | null => {
    if (!selected || !snapshotQuery.data) return null;
    return (
      snapshotQuery.data.entities.find(
        (entity) => entity.label === selected.label && entity.id === selected.id,
      ) ?? null
    );
  }, [selected, snapshotQuery.data]);

  const selectedEdges = useMemo(() => {
    if (!selected || !snapshotQuery.data) return [];
    return snapshotQuery.data.edges.filter(
      (edge) =>
        (edge.from_label === selected.label && edge.from_id === selected.id) ||
        (edge.to_label === selected.label && edge.to_id === selected.id),
    );
  }, [selected, snapshotQuery.data]);

  const handleSelect = (next: KnowledgeGraphSelection | null) => {
    setSelected(next);
    if (next) {
      setSideTab('details');
    }
  };

  const chatMutation = useMutation({
    mutationFn: (input: { messages: KnowledgeChatMessage[]; allow_mutations?: boolean }) =>
      chatKnowledge(input),
    onSuccess: async (result) => {
      if (result.reply) {
        setMessages((prev) => [...prev, { role: 'assistant', content: result.reply }]);
      }
      setPending(result.pending_mutations ?? []);
      if (result.graph_changed) {
        await queryClient.invalidateQueries({ queryKey: ['knowledge', 'export'] });
        toast.success('Knowledge graph updated');
      }
      if (result.applied?.length) {
        toast.message(`Applied ${result.applied.length} change(s)`);
      }
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : 'Knowledge chat failed';
      toast.error(message);
    },
  });

  const send = (content: string, allowMutations = false) => {
    const trimmed = content.trim();
    if (!trimmed || chatMutation.isPending) return;
    const nextMessages: KnowledgeChatMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setDraft('');
    if (!allowMutations) {
      setPending([]);
    }
    chatMutation.mutate({ messages: nextMessages, allow_mutations: allowMutations });
  };

  const handleBackup = async () => {
    if (snapshotBusy) return;
    setSnapshotBusy(true);
    try {
      const snapshot = snapshotQuery.data ?? (await exportKnowledge());
      downloadSnapshotFile(snapshot);
      toast.success(
        `Downloaded ${snapshot.total_entities ?? snapshot.entities.length} entities`,
      );
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Backup failed';
      toast.error(message);
    } finally {
      setSnapshotBusy(false);
    }
  };

  const handleRestoreFile = async (file: File | undefined) => {
    if (!file || snapshotBusy) return;
    setSnapshotBusy(true);
    try {
      const raw = await file.text();
      const { entities, edges } = parseSnapshotFile(raw);
      const result = await importKnowledgeSnapshot({
        entities,
        edges,
        clear_first: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['knowledge', 'export'] });
      setSelected(null);
      toast.success(
        `Restored ${result.entities_upserted} entities` +
          (result.cleared ? ` (cleared ${result.cleared})` : ''),
      );
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Restore failed';
      toast.error(message);
    } finally {
      setSnapshotBusy(false);
      if (restoreInputRef.current) {
        restoreInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PageHeader
        title="Knowledge"
        description="Browse the knowledge graph and edit it through a confirm-gated chat."
        actions={
          <div className="flex items-center gap-2">
            <input
              ref={restoreInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(event) => {
                void handleRestoreFile(event.target.files?.[0]);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={snapshotBusy}
                render={<Button type="button" variant="outline" size="sm" />}
              >
                Snapshot
                <ChevronDown data-icon="inline-end" className="size-3.5 opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-40">
                <DropdownMenuItem
                  disabled={snapshotBusy}
                  onClick={() => {
                    void handleBackup();
                  }}
                >
                  <Download className="size-3.5" />
                  Backup
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={snapshotBusy}
                  onClick={() => restoreInputRef.current?.click()}
                >
                  <Upload className="size-3.5" />
                  Restore
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => snapshotQuery.refetch()}
              disabled={snapshotQuery.isFetching || snapshotBusy}
            >
              Refresh graph
            </Button>
          </div>
        }
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="min-h-0 min-w-0 flex-1">
          {snapshotQuery.isError ? (
            <div className="flex h-full items-center justify-center text-sm text-destructive">
              Failed to load knowledge export
            </div>
          ) : (
            <KnowledgeGraphCanvas
              snapshot={snapshotQuery.data}
              selected={selected}
              onSelect={handleSelect}
            />
          )}
        </div>

        <aside className="flex min-h-0 w-full max-w-md shrink-0 flex-col border-l bg-background">
          <Tabs
            value={sideTab}
            onValueChange={(value) => {
              if (value === 'chat' || value === 'details') setSideTab(value);
            }}
            className="flex h-full min-h-0 flex-col gap-0"
          >
            <div className="shrink-0 border-b px-3 pt-3">
              <TabsList variant="line" className="w-full">
                <TabsTrigger value="chat" className="flex-1 after:bottom-0">
                  Chat
                </TabsTrigger>
                <TabsTrigger value="details" className="flex-1 after:bottom-0">
                  Details
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="chat"
              className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden"
            >
              <KnowledgeChatPanel
                messages={messages}
                pending={pending}
                draft={draft}
                busy={chatMutation.isPending}
                onDraftChange={setDraft}
                onSend={() => send(draft, false)}
                onConfirm={() => send('Confirm the proposed mutations.', true)}
                onReject={() => {
                  setPending([]);
                  setMessages((prev) => [
                    ...prev,
                    { role: 'user', content: 'Reject the proposed mutations.' },
                    { role: 'assistant', content: 'Understood — no changes were applied.' },
                  ]);
                }}
              />
            </TabsContent>

            <TabsContent
              value="details"
              className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden"
            >
              <KnowledgeDetailsPanel entity={selectedEntity} edges={selectedEdges} />
            </TabsContent>
          </Tabs>
        </aside>
      </div>
    </div>
  );
}
